import {
  ACTION_TONE_RING_CLASSES,
  FOCUS_RING_TONE_CLASSES,
} from './shared-classes';

// Model A axis unions. Hand-written literal unions (the segmented-control.tsx
// pattern) so component props read as exact enums regardless of how the cva
// `VariantProps` widens them.
//
//   • emphasis (treatment / "how loud"): filled · soft · outline · quiet · ghost
//   • tone     (intent / "what colour"): neutral · brand · danger · success   ← actions
//   • tone     (status):                 neutral · brand · danger · success · warning · info  ← Badge/Chip/Alert
//
// emphasis is PURE treatment — it carries no colour. Colour comes entirely from
// `tone`. So a bare action is `filled` + `neutral`; the brand CTA is just
// `tone="brand"`. The negative/critical role is `danger` (`destructive` is gone).
export type Emphasis = 'filled' | 'soft' | 'outline' | 'quiet' | 'ghost';
export type ActionTone = 'neutral' | 'brand' | 'danger' | 'success';
export type StatusTone =
  | 'neutral'
  | 'brand'
  | 'danger'
  | 'success'
  | 'warning'
  | 'info';

// Every class is written out literally so Tailwind's source scan generates the
// utilities (no dynamic `bg-${x}` interpolation).

// An outline or ghost action inside a surface that marks itself as a tint (the
// `alert-actions` row of a soft or outline Alert) drops to the tone's
// `-muted-foreground` step. The bare tone on its own tint is 4.29:1 for danger
// and 4.31:1 for brand in the docs theme, both under the 4.5:1 text minimum;
// the muted step measures above 7:1 on every tint. `scripts/verify-token-contrast.mjs`
// measures both steps on every tint.
const ON_TINT_TEXT_CLASSES = {
  brand: 'group-data-[on-tint]/on-tint:text-primary-muted-foreground',
  danger: 'group-data-[on-tint]/on-tint:text-danger-muted-foreground',
  success: 'group-data-[on-tint]/on-tint:text-success-muted-foreground',
} as const;

// An action inside a surface that marks itself as a solid tone (a `filled`
// Alert) has no tone colour left to work with. `text-danger` on `--danger` is
// 1.00:1, the matching `border-danger` outline disappears into the fill, and a
// `filled` danger Button is a `--danger` slab on `--danger` with a 1.00:1 edge.
// The `-muted-foreground` swap the tint rule uses does not help either, because
// a muted step is itself a tint colour.
//
// So the action inverts. The `on-solid:` variant is declared in
// `packages/ui/public/css/styles-base.css`; it keys on the `data-on-solid` the
// Alert root sets, and scopes itself to the nearest surface so a soft Alert
// nested inside a filled one keeps its own treatment.
//
// These classes key on `emphasis` alone: none of them names a tone, so one
// compound per emphasis covers every tone, and a future fourth `ActionTone`
// cannot miss one.

// outline and ghost carry no surface, so they take `currentColor` — which the
// Alert has already set to the tone's own `-foreground`. That measures 4.53:1
// at worst (`--success-foreground` on `--success`, `ui` light) across both
// themes, above the 4.5:1 text minimum and the 3:1 WCAG 1.4.11 holds the
// outline to. The ring drops its offset here, because `--ring-offset` paints
// that gap in the page colour, which is not the colour behind an action sitting
// on a solid tone.
//
// This covers the neutral tone as well, unlike the tint rule: neutral's
// `text-foreground` is 1.59:1 on a solid neutral surface and never reaches
// 4.5:1 on any of the others.
const ON_SOLID_BARE_CLASSES = [
  'on-solid:text-current',
  'on-solid:hover:bg-current/10',
  'on-solid:hover:text-current',
  'on-solid:data-[active=true]:bg-current/10',
  'on-solid:data-[active=true]:text-current',
  'on-solid:focus-visible:ring-current',
  'on-solid:focus-visible:ring-offset-0',
] as const;

// The same, plus the outline's own border.
const ON_SOLID_OUTLINE_CLASSES = [
  ...ON_SOLID_BARE_CLASSES,
  'on-solid:border-current',
] as const;

// filled and soft do bring a surface, so they invert to the page colour and its
// text: a light button on the solid tone in the light theme, a dark one in the
// dark theme. `--background` measures 3.29:1 at worst on a solid tone (light
// `--warning`), above the 3:1 that identifies the control, and the label pair is
// `--foreground` on `--background` — the page's own text, 17.27:1 at worst. The
// ring keeps its offset here, because that gap is painted in `--background`,
// which is now the button's own fill.
//
// The hover fill is the one step that does not clear 3:1 against the surface:
// `--neutral-muted` on `--warning` is 2.87:1 in the light theme. What keeps the
// control identified in that state is `border-background`, which hover does not
// override — so do not add a hover border here without re-measuring.
//
// soft takes the same treatment rather than a fainter one: its own tint cannot
// hold an edge on a solid tone (`--warning-muted` on `--warning` is 2.82:1), and
// a solid surface has room for two action levels, not four.
const ON_SOLID_SURFACE_CLASSES = [
  'on-solid:border-background',
  'on-solid:bg-background',
  'on-solid:text-foreground',
  'on-solid:hover:bg-neutral-muted',
  'on-solid:hover:text-foreground',
  'on-solid:focus-visible:ring-current',
] as const;

// An action marked `aria-invalid` keeps its error edge on a solid tone. The
// base rules (`aria-invalid:border-danger` and `aria-invalid:ring-danger` in
// shared-classes.ts) are (0,2,0) and the `on-solid:` rules above are (0,5,0), so
// the inversion outranked them and the danger border vanished inside a filled
// Alert. These carry both halves, which makes them (0,6,0), so the error state
// wins again.
//
// `--danger` needs a surface it can be read on: on the solid tone itself it is
// 1.00:1 in a danger Alert and 2.10:1 at best on any other. So an invalid
// action takes the page surface whatever its emphasis. `filled` and `soft`
// already do that above, and `outline` and `ghost` switch to it only when
// invalid, which is the one state where their bare treatment has no colour left
// to carry the signal.
//
// The hover and focus-visible compounds are spelled out because the rules they
// have to beat (`on-solid:hover:bg-current/10`, `on-solid:hover:bg-neutral-muted`
// and `on-solid:focus-visible:ring-current`) are also (0,6,0), and a tie is
// settled by Tailwind's own variant ordering rather than by this list.
const ON_SOLID_INVALID_CLASSES = [
  'on-solid:aria-invalid:border-danger',
  'on-solid:aria-invalid:bg-background',
  'on-solid:aria-invalid:text-foreground',
  'on-solid:aria-invalid:hover:bg-neutral-muted',
  'on-solid:aria-invalid:hover:text-foreground',
  'on-solid:aria-invalid:ring-danger',
  'on-solid:aria-invalid:focus-visible:ring-danger',
] as const;

// ───────────────────────── Action axes (emphasis × tone) ─────────────────────────
// Button, Link(type="button"), StatusButton. The base element carries
// `border border-transparent`; filled/soft set their own surface, outline sets a
// border in the tone, quiet sets the hairline `border` token, ghost stays bare.
// The focus ring follows the tone.
//
// `quiet` is an emphasis, not an option on `outline`: it is a treatment one step
// below outline and one above ghost, and it takes its colour from `tone` like
// every other emphasis. The border stays the neutral hairline in every tone;
// only the label and the hover carry the tone. A neutral quiet label is the
// main text colour; the hairline border and muted icons are what make it
// quieter than a neutral outline.
//
// `success` is an action tone for positive actions ("Mark hired"). Toggle keeps
// its own three tones (`ACTION_TONE_RING_CLASSES`); the `success` ring is added
// here only.
const emphasisActionVariants = {
  variants: {
    emphasis: {
      filled: '',
      soft: '',
      outline: 'bg-transparent',
      quiet: 'bg-transparent border-border',
      ghost: 'bg-transparent',
    },
    tone: {
      ...ACTION_TONE_RING_CLASSES,
      success: FOCUS_RING_TONE_CLASSES.success,
    },
  },
  compoundVariants: [
    // filled — high emphasis, solid surface.
    {
      emphasis: 'filled',
      tone: 'neutral',
      className: [
        'border-neutral',
        'bg-neutral',
        'text-neutral-foreground',
        'hover:bg-neutral-hover',
      ],
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      className: [
        'border-primary',
        'bg-primary',
        'text-primary-foreground',
        'hover:bg-primary-hover',
      ],
    },
    {
      emphasis: 'filled',
      tone: 'danger',
      className: [
        'border-danger',
        'bg-danger',
        'text-danger-foreground',
        'hover:bg-danger-hover',
      ],
    },
    {
      emphasis: 'filled',
      tone: 'success',
      className: [
        'border-success',
        'bg-success',
        'text-success-foreground',
        'hover:bg-success-hover',
      ],
    },
    // soft — medium emphasis, muted tinted surface.
    {
      emphasis: 'soft',
      tone: 'neutral',
      className: [
        'border-neutral-muted',
        'bg-neutral-muted',
        'text-neutral-muted-foreground',
        'hover:bg-neutral-muted-hover',
      ],
    },
    {
      emphasis: 'soft',
      tone: 'brand',
      className: [
        'border-primary-muted',
        'bg-primary-muted',
        'text-primary-muted-foreground',
        'hover:bg-primary-muted-hover',
      ],
    },
    {
      emphasis: 'soft',
      tone: 'danger',
      className: [
        'border-danger-muted',
        'bg-danger-muted',
        'text-danger-muted-foreground',
        'hover:bg-danger-muted-hover',
      ],
    },
    {
      emphasis: 'soft',
      tone: 'success',
      className: [
        'border-success-muted',
        'bg-success-muted',
        'text-success-muted-foreground',
        'hover:bg-success-muted-hover',
      ],
    },
    // outline — medium-low, bordered on a transparent surface.
    {
      emphasis: 'outline',
      tone: 'neutral',
      className: [
        'border-neutral',
        'text-foreground',
        'hover:bg-neutral-muted',
        'data-[active=true]:bg-neutral-muted',
      ],
    },
    {
      emphasis: 'outline',
      tone: 'brand',
      className: [
        'border-primary',
        'text-primary',
        ON_TINT_TEXT_CLASSES.brand,
        'hover:bg-primary-muted',
        'hover:text-primary-muted-foreground',
        'data-[active=true]:bg-primary-muted',
        'data-[active=true]:text-primary-muted-foreground',
      ],
    },
    {
      emphasis: 'outline',
      tone: 'danger',
      className: [
        'border-danger',
        'text-danger',
        ON_TINT_TEXT_CLASSES.danger,
        'hover:bg-danger-muted',
        'hover:text-danger-muted-foreground',
        'data-[active=true]:bg-danger-muted',
        'data-[active=true]:text-danger-muted-foreground',
      ],
    },
    {
      emphasis: 'outline',
      tone: 'success',
      className: [
        'border-success',
        'text-success',
        ON_TINT_TEXT_CLASSES.success,
        'hover:bg-success-muted',
        'hover:text-success-muted-foreground',
        'data-[active=true]:bg-success-muted',
        'data-[active=true]:text-success-muted-foreground',
      ],
    },
    // quiet — low-medium, hairline border on a transparent surface.
    {
      emphasis: 'quiet',
      tone: 'neutral',
      // The label is the main text colour, because a grey label on a button
      // reads as disabled. Its icons keep the muted grey at rest and turn to
      // the label colour on hover. The icon rules sit in `:where()`, so they
      // carry no specificity and a colour the caller sets on an icon wins.
      // Inside a filled Alert the icons follow the inverted label instead.
      className: [
        'text-foreground',
        '[:where(&)_:where(svg)]:text-neutral-muted-foreground',
        'hover:bg-neutral-muted',
        'hover:[:where(&)_:where(svg)]:text-foreground',
        'data-[active=true]:bg-neutral-muted',
        'data-[active=true]:[:where(&)_:where(svg)]:text-foreground',
        'on-solid:[&_svg]:text-current',
      ],
    },
    {
      emphasis: 'quiet',
      tone: 'brand',
      className: [
        'text-primary',
        ON_TINT_TEXT_CLASSES.brand,
        'hover:bg-primary-muted',
        'hover:text-primary-muted-foreground',
        'data-[active=true]:bg-primary-muted',
        'data-[active=true]:text-primary-muted-foreground',
      ],
    },
    {
      emphasis: 'quiet',
      tone: 'danger',
      className: [
        'text-danger',
        ON_TINT_TEXT_CLASSES.danger,
        'hover:bg-danger-muted',
        'hover:text-danger-muted-foreground',
        'data-[active=true]:bg-danger-muted',
        'data-[active=true]:text-danger-muted-foreground',
      ],
    },
    {
      emphasis: 'quiet',
      tone: 'success',
      className: [
        'text-success',
        ON_TINT_TEXT_CLASSES.success,
        'hover:bg-success-muted',
        'hover:text-success-muted-foreground',
        'data-[active=true]:bg-success-muted',
        'data-[active=true]:text-success-muted-foreground',
      ],
    },
    // ghost — low emphasis, text only until hover.
    {
      emphasis: 'ghost',
      tone: 'neutral',
      className: [
        'text-foreground',
        'hover:bg-neutral-muted',
        'data-[active=true]:bg-neutral-muted',
      ],
    },
    {
      emphasis: 'ghost',
      tone: 'brand',
      className: [
        'text-primary',
        ON_TINT_TEXT_CLASSES.brand,
        'hover:bg-primary-muted',
        'hover:text-primary-muted-foreground',
        'data-[active=true]:bg-primary-muted',
        'data-[active=true]:text-primary-muted-foreground',
      ],
    },
    {
      emphasis: 'ghost',
      tone: 'danger',
      className: [
        'text-danger',
        ON_TINT_TEXT_CLASSES.danger,
        'hover:bg-danger-muted',
        'hover:text-danger-muted-foreground',
        'data-[active=true]:bg-danger-muted',
        'data-[active=true]:text-danger-muted-foreground',
      ],
    },
    {
      emphasis: 'ghost',
      tone: 'success',
      className: [
        'text-success',
        ON_TINT_TEXT_CLASSES.success,
        'hover:bg-success-muted',
        'hover:text-success-muted-foreground',
        'data-[active=true]:bg-success-muted',
        'data-[active=true]:text-success-muted-foreground',
      ],
    },
    // The solid-surface inversion, keyed on emphasis alone (see the
    // `ON_SOLID_*` lists above). A compound matches on exactly the keys it
    // names, so these apply across every tone. In `link.ts` every inherited
    // compound is remapped to `type: 'button'`, so they never reach an inline
    // link. Every emphasis also carries `ON_SOLID_INVALID_CLASSES`, because the
    // inversion outranks the base `aria-invalid` rules on any of them. One
    // compound with `emphasis: ['outline', ...]` would say that once, but
    // `button.ts` and `link.ts` remap every inherited compound and both expect
    // a single emphasis, so the list is spread into each instead.
    {
      emphasis: 'outline',
      className: [...ON_SOLID_OUTLINE_CLASSES, ...ON_SOLID_INVALID_CLASSES],
    },
    {
      emphasis: 'quiet',
      className: [...ON_SOLID_OUTLINE_CLASSES, ...ON_SOLID_INVALID_CLASSES],
    },
    {
      emphasis: 'ghost',
      className: [...ON_SOLID_BARE_CLASSES, ...ON_SOLID_INVALID_CLASSES],
    },
    {
      emphasis: 'filled',
      className: [...ON_SOLID_SURFACE_CLASSES, ...ON_SOLID_INVALID_CLASSES],
    },
    {
      emphasis: 'soft',
      className: [...ON_SOLID_SURFACE_CLASSES, ...ON_SOLID_INVALID_CLASSES],
    },
  ] as const,
};

// ───────────────────────── Status axis (variant × tone) ─────────────────────────
// Badge, Chip, Alert. `variant` is the structural treatment; `tone` is the
// semantic status. success/warning/info live here (and only here). `brand` is
// the decorative accent (the terracotta/primary ramp) — for tags and category
// pills that aren't a status; it mirrors the action `tone="brand"`.
const statusToneVariants = {
  variants: {
    variant: {
      filled: '',
      outline: 'bg-transparent border',
      soft: 'border',
    },
    tone: {
      neutral: '',
      brand: '',
      danger: '',
      success: '',
      warning: '',
      info: '',
    },
  },
  compoundVariants: [
    // soft — muted tinted surface (the default).
    {
      variant: 'soft',
      tone: 'neutral',
      className:
        'bg-neutral-muted text-neutral-muted-foreground border-neutral/30',
    },
    {
      variant: 'soft',
      tone: 'brand',
      className:
        'bg-primary-muted text-primary-muted-foreground border-primary/30',
    },
    {
      variant: 'soft',
      tone: 'danger',
      className:
        'bg-danger-muted text-danger-muted-foreground border-danger/30',
    },
    {
      variant: 'soft',
      tone: 'success',
      className:
        'bg-success-muted text-success-muted-foreground border-success/30',
    },
    {
      variant: 'soft',
      tone: 'warning',
      className:
        'bg-warning-muted text-warning-muted-foreground border-warning/30',
    },
    {
      variant: 'soft',
      tone: 'info',
      className: 'bg-info-muted text-info-muted-foreground border-info/30',
    },
    // filled — solid status surface.
    {
      variant: 'filled',
      tone: 'neutral',
      className: 'bg-neutral text-neutral-foreground',
    },
    {
      variant: 'filled',
      tone: 'brand',
      className: 'bg-primary text-primary-foreground',
    },
    {
      variant: 'filled',
      tone: 'danger',
      className: 'bg-danger text-danger-foreground',
    },
    {
      variant: 'filled',
      tone: 'success',
      className: 'bg-success text-success-foreground',
    },
    {
      variant: 'filled',
      tone: 'warning',
      className: 'bg-warning text-warning-foreground',
    },
    {
      variant: 'filled',
      tone: 'info',
      className: 'bg-info text-info-foreground',
    },
    // outline — bordered, foreground text.
    {
      variant: 'outline',
      tone: 'neutral',
      className: 'border-neutral text-foreground',
    },
    {
      variant: 'outline',
      tone: 'brand',
      className: 'border-primary text-foreground',
    },
    {
      variant: 'outline',
      tone: 'danger',
      className: 'border-danger text-foreground',
    },
    {
      variant: 'outline',
      tone: 'success',
      className: 'border-success text-foreground',
    },
    {
      variant: 'outline',
      tone: 'warning',
      className: 'border-warning text-foreground',
    },
    {
      variant: 'outline',
      tone: 'info',
      className: 'border-info text-foreground',
    },
  ] as const,
};

export { emphasisActionVariants, statusToneVariants };
